import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';

import determineCalendarModes from 'bl/salon/determineCalendarModes.bl';
import THEME_MODE from 'constants/themeMode.const';
import { selectCurrentSalonCalendarMode } from 'selectors/currentSalon.selectors';
import withOptions from 'utils/functions/withOptions.util';
import logger from 'utils/logger.util';
import fromComponentToName from 'utils/react/fromComponentToName.util';
import C from 'utils/values/constant.util';

const L = logger('ThemeProvider');

const MB = THEME_MODE.bright;
const MD = THEME_MODE.dark;
const ML = THEME_MODE.light;

const SUPER_DEFAULT = C(determineCalendarModes({ calendarMode: null, options: { hasBright: false, defaultMode: ML } }));

const Context = React.createContext();

const mapStateToProps = (state) => ({
  calendarMode: selectCurrentSalonCalendarMode(state),
});

export const withTheme = withOptions((options) => (Component) => {
  // find out the name of the wrapped component or use a default one
  const displayName = `withTheme(${fromComponentToName(Component)})`;

  // create the wrapper component and use it to render the wrapped one and also
  // remove the props added by connect from the props for the child component
  const WithTheme = (props) => {
    const { dispatch, calendarMode, ...extra } = props;
    const modes = determineCalendarModes({ calendarMode, options });

    return <Component {...extra} {...modes} />;
  };

  // static fields must be copied from the wrapped to the wrapper
  hoistNonReactStatics(WithTheme, Component);

  // nice display name for the wrapper
  WithTheme.displayName = displayName;

  // need to wire up few more wrappers for the extra props needed
  return connect(mapStateToProps)(WithTheme);
});

const Provider = ({ calendarMode, children }) => {
  const value = useMemo(
    () => {
      L.info('()', 'calendarMode:', calendarMode);

      const brightB = determineCalendarModes({ calendarMode, options: { hasBright: true, defaultMode: MB } });
      const brightD = determineCalendarModes({ calendarMode, options: { hasBright: true, defaultMode: MD } });
      const brightL = determineCalendarModes({ calendarMode, options: { hasBright: true, defaultMode: ML } });

      const plainD = determineCalendarModes({ calendarMode, options: { hasBright: false, defaultMode: MD } });
      const plainL = determineCalendarModes({ calendarMode, options: { hasBright: false, defaultMode: ML } });

      return { calendarMode, brightB, brightD, brightL, plainD, plainL };
    },
    [calendarMode],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const ThemeProvider = connect(mapStateToProps)(Provider);

export const useTheme = () => {
  const value = useContext(Context);

  return value ? value.plainL : SUPER_DEFAULT;
};

export const useThemeWithBright = () => {
  const value = useContext(Context);

  return value ? value.brightL : SUPER_DEFAULT;
};

export const useCalendarMode = () => {
  const value = useContext(Context);

  return value.calendarMode;
};
